import styles from './styles.module.css';
import {styledMod} from '@/utils/styled-mod';
import {OutlinedInput, Typography} from '@mui/material';
import {SelectTokenTitle} from '@/components/formatted-messages';
import FlexContainer from '@/components/common/flex-container';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import type {FunctionComponent} from 'react';
import {RootContext} from '@/main.tsx';
import Locale from '@/components/enums/locale.ts';
import {createPortal} from 'react-dom';
import TokenPresetButtons from '@/components/swap-widget-new/token-preset-buttons';
import type TokenMetadataItem from '@/components/tokens/types/token-metadata-item.ts';
import {SwapWidgetTokensList} from '@/components/swap-widget-new/swap-widget-tokens-list';
import {useDebounce} from 'use-debounce';
import {SwapWidgetNewTabs} from '@/components/swap-widget-new/swap-widget-new-tabs';
import IconButton from '@/components/common/icon-button';
import CloseIcon from '@mui/icons-material/Close';
import {SwapType} from '@/components/swap-widget-new/types/swap-type.ts';
import {SwapWidgetContext} from '@/pages/swap-widget';
import type {SwapWidgetContextProps} from '@/pages/swap-widget/types/swap-widget-context-props.ts';

const SwapWidgetSelectContainer = styledMod('div', styles['swap-widget-select-container']);
const SwapWidgetSearchInputContainer = styledMod('div', styles['swap-widget-search-input-container']);
const SwapWidgetTokenPresetsContainer = styledMod(
  'div',
  styles['swap-widget-tokens-presets-container'],
);

type SwapWidgetSelectProps = {
  type: SwapType,
  tokensMetadata: TokenMetadataItem[];
  onTokenAddressChange: (tokenName: string, tokenAddress: string, imgSrc: string) => void;
  onTokenListClose: () => void;
  rectParameters: DOMRect | null;
  onTokenAddressFavoritesChange: (elementSymbol: string) => void;
};


export const SwapWidgetSelect: FunctionComponent<SwapWidgetSelectProps> = (props: SwapWidgetSelectProps) => {
  const context = useContext(RootContext);
  const [tokenName, setTokenName] = useState<string | undefined>(undefined);
  const [top, setTop] = useState<number>(0);
  const [left, setLeft] = useState<number>(0)
  const [width, setWidth] = useState<number>(0);
  const selectContainerRef = useRef<HTMLDivElement | null>(null);
  const [tabs] = useState<string[]>(['All assets', 'Favorites']);
  const [activeTab, setActiveTab] = useState<number>(0);

  const [debouncedTokenName] = useDebounce(tokenName, 500);

  const swapWidgetContent = useContext<SwapWidgetContextProps>(SwapWidgetContext);

  useEffect(() => {
    const onDialogClose = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') {
        props.onTokenListClose();
      }
    }

    document.addEventListener('keyup', onDialogClose);
    return () => {
      document.removeEventListener('keyup', onDialogClose);
    }
  }, [props.onTokenListClose]);

  useEffect(() => {

    // document.addEventListener('mousedown', onDialogClose);
    // document.addEventListener('touchstart', onDialogClose);
    return () => {
      // document.removeEventListener('mousedown', onDialogClose);
      // document.addEventListener('touchstart', onDialogClose);
    }
  }, [props.onTokenListClose]);

  useEffect(() => {
    if (props.rectParameters) {
      setTop(props.rectParameters.top);
      setLeft(props.rectParameters.left);
      setWidth(props.rectParameters.width);
    }
  }, [props.rectParameters])

  const searchByNameTitle: string = context.locale === Locale.RU ? 'Поиск по названию' : 'Search by name';

  const onTokenNameChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const element = evt.target;
    setTokenName(element.value);
  };

  const tokensMetadataMemomized: TokenMetadataItem[] = useMemo(() => {
    if (debouncedTokenName !== undefined) {
      return props.tokensMetadata.filter((tokenElement: TokenMetadataItem) =>
        tokenElement.symbol?.toLowerCase().includes(debouncedTokenName.toLowerCase()),
      );
    }
    return props.tokensMetadata;
  }, [props.tokensMetadata, debouncedTokenName]);

  const tokensMetadataFavoritesMemomized: TokenMetadataItem[] = useMemo(() => {
    if (debouncedTokenName !== undefined) {
      return props.tokensMetadata.filter((tokenElement: TokenMetadataItem) =>
        tokenElement.isFavorite && tokenElement.symbol?.toLowerCase().includes(debouncedTokenName.toLowerCase()),
      );
    }
    return props.tokensMetadata.filter((tokenElement: TokenMetadataItem) => tokenElement.isFavorite);
  }, [props.tokensMetadata, debouncedTokenName]);


  const onTokenAddressChange = (tokenName: string, tokenAddress: string, imgSrc: string) => {
    props.onTokenListClose();
    props.onTokenAddressChange(tokenName, tokenAddress, imgSrc);
  }

  const onTabChange = (tabNumber: number) => {
    setActiveTab(tabNumber);
  }

  const onTokenListClose = () => {
    props.onTokenListClose();
  }


  const activeTokenName: string = useMemo(
    () => {
      if (props.type === 'send') {
        return props.tokensMetadata?.find((element: TokenMetadataItem) => element.address === swapWidgetContent.sendTokenAddress)?.symbol
      }
      return props.tokensMetadata?.find((element: TokenMetadataItem) => element.address === swapWidgetContent.receiveTokenAddress)?.symbol
    }, [props.tokensMetadata, swapWidgetContent.sendTokenAddress, swapWidgetContent.receiveTokenAddress]
  ) ?? '';

  return createPortal(
    <SwapWidgetSelectContainer ref={selectContainerRef} style={{left: left, top, width}}>
      <FlexContainer margin="0 0 30px 0" justifycontent="space-between">
        <Typography fontWeight={900} variant="h5">
          <SelectTokenTitle/>
        </Typography>
        <IconButton onClick={onTokenListClose}>
          <CloseIcon/>
        </IconButton>
      </FlexContainer>
      <SwapWidgetSearchInputContainer>
        <OutlinedInput
          className={styles['']}
          fullWidth={true}
          sx={{
            height: '50px',
            borderradius: '15px',
            border: 'none',
          }}
          placeholder={searchByNameTitle}
          onChange={onTokenNameChange}
          value={tokenName}
        />
      </SwapWidgetSearchInputContainer>
      <SwapWidgetTokenPresetsContainer>
        <TokenPresetButtons activeToken={activeTokenName} tokensMetadata={props.tokensMetadata}
                            onTokenAddressChange={onTokenAddressChange}/>
      </SwapWidgetTokenPresetsContainer>
      <SwapWidgetNewTabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange}/>
      {activeTab === 0 && (tokensMetadataMemomized.length > 0 ? (
        <SwapWidgetTokensList
          onTokenAddressChange={onTokenAddressChange}
          activeElement={activeTokenName}
          tokensMetadata={tokensMetadataMemomized}
          onTokenAddressFavoritesChange={props.onTokenAddressFavoritesChange}
        />
      ) : <p>No assets found</p>)}
      {activeTab === 1 && (tokensMetadataFavoritesMemomized.length > 0 ? (
        <SwapWidgetTokensList
          onTokenAddressChange={onTokenAddressChange}
          activeElement={activeTokenName}
          tokensMetadata={tokensMetadataFavoritesMemomized}
          onTokenAddressFavoritesChange={props.onTokenAddressFavoritesChange}
        />) : <p>Your favorites list is empties</p>)}
    </SwapWidgetSelectContainer>,
    document.body,
  );
};
